import React, { useRef } from 'react';
import { MapPin, Map, Pencil } from 'lucide-react';
import type { Address } from '../../types/shared';
import { getGoogleMapsUrl } from '../../types/shared';
import { useMapOverlay } from '../../components/shared/MapOverlay';
import type { InfoChipConfig, ActionButtonConfig } from './MapOverlay/types';

export interface AddressCardProps {
  /** Address data */
  address?: Address | null;
  /** Title for the card */
  title?: string;
  /** Called when edit is requested */
  onEdit?: () => void;
  /** Show map button */
  showMapButton?: boolean;
  /** Show edit button */
  showEditButton?: boolean;
  /** Compact display (inline vs card) */
  compact?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Title shown in the map overlay header */
  mapTitle?: string;
  /** Info chips displayed in the map overlay info strip */
  mapInfoStrip?: InfoChipConfig[];
  /** Sidebar content for the map overlay */
  mapSidebar?: React.ReactNode;
  /** Footer action buttons for the map overlay */
  mapFooter?: ActionButtonConfig[];
}

/**
 * Address display card with map overlay integration
 *
 * Features:
 * - Structured address display
 * - Click address to open Google Maps
 * - Map button opens draggable map overlay panel
 * - Hover preview on address link
 * - Edit button triggers callback
 * - Compact mode for inline display
 */
export function AddressCard({
  address,
  title = 'Address',
  onEdit,
  showMapButton = true,
  showEditButton = true,
  compact = false,
  className = '',
  mapTitle,
  mapInfoStrip,
  mapSidebar,
  mapFooter,
}: AddressCardProps) {
  const mapTriggerRef = useRef<HTMLButtonElement>(null);
  const addressLinkRef = useRef<HTMLAnchorElement>(null);
  const { openMap, hoverProps, MapPanel, PreviewTooltip } = useMapOverlay();

  if (!address) {
    if (compact) {
      return (
        <button
          onClick={onEdit}
          className={`flex items-center gap-2 text-slate-400 hover:text-sky-600 group ${className}`}
        >
          <MapPin className="w-4 h-4" />
          <span className="text-sm italic">Add address</span>
          <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100" />
        </button>
      );
    }

    return (
      <div className={`bg-white rounded-xl border border-slate-300 shadow-sm p-4 ${className}`}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">
            {title}
          </h3>
        </div>
        <button
          onClick={onEdit}
          className="flex items-center gap-2 text-slate-400 hover:text-sky-600 group"
        >
          <MapPin className="w-4 h-4" />
          <span className="text-sm italic">Add address</span>
          <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100" />
        </button>
      </div>
    );
  }

  const mapsUrl = getGoogleMapsUrl(address);
  const addressHoverProps = hoverProps({ address });

  const handleOpenMap = () => {
    openMap({
      address,
      triggerRef: mapTriggerRef,
      title: mapTitle || title,
      infoStrip: mapInfoStrip,
      sidebar: mapSidebar,
      footer: mapFooter,
    });
  };

  // Compact display (inline)
  if (compact) {
    return (
      <>
        <div className={`flex items-center gap-2 ${className}`}>
          <a
            ref={addressLinkRef}
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-slate-500 hover:text-sky-600 group"
            {...addressHoverProps}
          >
            <MapPin className="w-4 h-4" />
            <span className="group-hover:underline">
              {address.street1 ? `${address.street1}, ` : ''}{address.city}, {address.state}
            </span>
          </a>
          {showMapButton && (
            <button
              ref={mapTriggerRef}
              onClick={handleOpenMap}
              className="p-1 text-slate-400 hover:text-sky-600 hover:bg-sky-50 rounded transition-colors"
              title="Open map"
            >
              <Map className="w-4 h-4" />
            </button>
          )}
          {showEditButton && onEdit && (
            <button
              onClick={onEdit}
              className="p-1 text-slate-400 hover:text-sky-600 hover:bg-sky-50 rounded transition-colors"
              title="Edit address"
            >
              <Pencil className="w-3 h-3" />
            </button>
          )}
        </div>
        {MapPanel}
        {PreviewTooltip}
      </>
    );
  }

  // Full card display
  return (
    <>
      <div className={`bg-white rounded-xl border border-slate-300 shadow-sm p-4 ${className}`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">
            {title}
          </h3>
          <div className="flex items-center gap-1">
            {showMapButton && (
              <button
                ref={mapTriggerRef}
                onClick={handleOpenMap}
                className="p-1 text-slate-400 hover:text-sky-600 hover:bg-sky-50 rounded transition-colors"
                title="Open map"
              >
                <Map className="w-4 h-4" />
              </button>
            )}
            {showEditButton && onEdit && (
              <button
                onClick={onEdit}
                className="p-1 text-slate-400 hover:text-sky-600 hover:bg-sky-50 rounded transition-colors"
                title="Edit address"
              >
                <Pencil className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <a
          ref={addressLinkRef}
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block group"
          {...addressHoverProps}
        >
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="text-slate-700 group-hover:text-sky-600">
                {address.street1}
              </p>
              {address.street2 && (
                <p className="text-slate-700 group-hover:text-sky-600">
                  {address.street2}
                </p>
              )}
              <p className="text-slate-700 group-hover:text-sky-600">
                {address.city}, {address.state} {address.zip}
              </p>
            </div>
          </div>
        </a>
      </div>
      {MapPanel}
      {PreviewTooltip}
    </>
  );
}

export default AddressCard;
